import React, { useState, useCallback, useMemo, useRef } from 'react'
import { Grid } from '@material-ui/core'
import PropTypes from 'prop-types'
import { useModifyQueryBlueprintDefinition } from '../../../../../../api/denali'
import SydTabs from '../../../../../commonDesign/SydTabs'
import OpalCard from '../../../../../opal/OpalCard'
import InlineCardHeader from '../../../../../molecules/Card/InlineCardHeader'
import SydButton from '../../../../../commonDesign/Button'
import BlueprintInfoSection from './BlueprintInfoSection'
import VersionControlCard from './VersionControlCard'
import CurrentConfigurationSection from './CurrentConfigurationSection'
import {
  PresentationListSidebar,
  PresentationDetailPanel,
  AddPresentationDialog
} from './presentationManagement'

const subTabs = {
  configuration: {
    label: 'Data Configuration',
    value: 'configuration'
  },
  presentation: {
    label: 'Presentations',
    value: 'presentation'
  }
}

function GeneralTab ({
  blueprint,
  selectedVersion,
  onSelectVersion,
  versionData,
  allVersions,
  isEditing,
  setIsEditing,
  onSave,
  onAddDefinition,
  onSetActive
}) {
  const { mutateAsync: modifyDefinition } = useModifyQueryBlueprintDefinition()
  const [currentSubTab, setCurrentSubTab] = useState('configuration')
  const addPresentationRef = useRef(null)

  // Use versionData (selected version) instead of blueprint for display
  const displayData = versionData || blueprint
  const isActiveVersion = !selectedVersion || selectedVersion.definitionVersionId === blueprint.definitionVersionId

  // Presentation management state
  const initialPresentationOptions = displayData.definition?.presentation?.options || {}
  const [localPresentationOptions, setLocalPresentationOptions] = useState(initialPresentationOptions)
  const [selectedPresentationOption, setSelectedPresentationOption] = useState(() => {
    const optionCodes = Object.keys(initialPresentationOptions)
    return optionCodes[0] || null
  })
  const [savingPresentation, setSavingPresentation] = useState(false)

  // Track presentation changes
  const isPresentationDirty = useMemo(() => {
    const original = JSON.stringify(displayData.definition?.presentation?.options || {})
    const current = JSON.stringify(localPresentationOptions)
    return original !== current
  }, [localPresentationOptions, displayData.definition])

  // All purposes from the current definition for the type editors
  const allPurposes = displayData.definition?.data?.purposes || {}

  const handleSaveConfiguration = useCallback(async (updates) => {
    await modifyDefinition({
      blueprintId: blueprint.blueprintId,
      definitionVersionId: selectedVersion?.definitionVersionId,
      data: updates.data
    })
    onSave() // Refresh blueprint
  }, [blueprint.blueprintId, selectedVersion, modifyDefinition, onSave])

  // Presentation handlers
  const handleAddPresentation = useCallback(({ optionCode, optionData }) => {
    setLocalPresentationOptions(prev => ({
      ...prev,
      [optionCode]: optionData
    }))
    setSelectedPresentationOption(optionCode)
  }, [])

  const handleRemovePresentation = useCallback((code) => {
    const optionData = localPresentationOptions[code]
    const confirmed = window.confirm(`Remove presentation "${optionData?.displayName || code}"?`)
    if (confirmed) {
      setLocalPresentationOptions(prev => {
        const updated = { ...prev }
        delete updated[code]
        return updated
      })
      if (selectedPresentationOption === code) {
        const remaining = Object.keys(localPresentationOptions).filter(k => k !== code)
        setSelectedPresentationOption(remaining[0] || null)
      }
    }
  }, [selectedPresentationOption, localPresentationOptions])

  const handleUpdatePresentationConfig = useCallback((optionCode, updatedConfig) => {
    setLocalPresentationOptions(prev => ({
      ...prev,
      [optionCode]: {
        ...prev[optionCode],
        config: updatedConfig
      }
    }))
  }, [])

  const handleSavePresentation = useCallback(async () => {
    try {
      setSavingPresentation(true)
      await modifyDefinition({
        blueprintId: blueprint.blueprintId,
        definitionVersionId: selectedVersion?.definitionVersionId,
        presentation: { options: localPresentationOptions }
      })
      onSave() // Refresh blueprint
    } catch (err) {
      console.error('Failed to save presentation:', err)
    } finally {
      setSavingPresentation(false)
    }
  }, [blueprint.blueprintId, selectedVersion, localPresentationOptions, modifyDefinition, onSave])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <BlueprintInfoSection
          blueprint={blueprint}
          onSave={onSave}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <VersionControlCard
          blueprint={blueprint}
          allVersions={allVersions}
          selectedVersion={selectedVersion}
          onSelectVersion={onSelectVersion}
          onSetActive={onSetActive}
          onAddDefinition={onAddDefinition}
        />
      </Grid>

      <Grid item xs={12}>
        <SydTabs.Group
          tabOptions={subTabs}
          onChange={setCurrentSubTab}
          defaultValue={currentSubTab}
          key='general_sub_tabs'
        >
          <SydTabs.List />

          <SydTabs.Tab value='configuration'>
            <CurrentConfigurationSection
              key={displayData.definition?.definitionId || 'no-definition'}
              blueprint={displayData}
              isActiveVersion={isActiveVersion}
              onSave={handleSaveConfiguration}
            />
          </SydTabs.Tab>

          <SydTabs.Tab value='presentation'>
            <OpalCard size='sm'>
              <InlineCardHeader
                action={
                  isPresentationDirty ? (
                    <SydButton
                      onClick={handleSavePresentation}
                      processing={savingPresentation}
                      variant='primary'
                      size='sm'
                      icon='check'
                    >
                      {isActiveVersion ? 'Save Changes (Active)' : 'Save Changes (Draft)'}
                    </SydButton>
                  ) : null
                }
              >
                Presentation Options
              </InlineCardHeader>
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px', minHeight: '400px' }}>
                  <PresentationListSidebar
                    presentationOptions={localPresentationOptions}
                    selectedOption={selectedPresentationOption}
                    onSelectOption={setSelectedPresentationOption}
                    onAddOption={() => addPresentationRef.current.open()}
                    onRemoveOption={handleRemovePresentation}
                  />
                  <PresentationDetailPanel
                    optionCode={selectedPresentationOption}
                    optionData={localPresentationOptions[selectedPresentationOption]}
                    allPurposes={allPurposes}
                    onUpdateConfig={handleUpdatePresentationConfig}
                  />
                </div>
              </div>
            </OpalCard>

            <AddPresentationDialog ref={addPresentationRef} onComplete={handleAddPresentation} />
          </SydTabs.Tab>
        </SydTabs.Group>
      </Grid>
    </Grid>
  )
}

GeneralTab.propTypes = {
  blueprint: PropTypes.object.isRequired,
  selectedVersion: PropTypes.object,
  onSelectVersion: PropTypes.func.isRequired,
  versionData: PropTypes.object,
  allVersions: PropTypes.array,
  isEditing: PropTypes.bool.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onAddDefinition: PropTypes.func.isRequired,
  onSetActive: PropTypes.func.isRequired
}

export default GeneralTab
